namespace Solovis.Reports.Parameters.HorizonPerformance {

  export class Options {
    targetCurrency: any;
    benchmark: boolean = true;
    secondaryBenchmark: boolean;
    benchmarkExcessReturn: boolean = true;
    twrSettingsSourceAsset: any;
    bookValues: boolean;
    blendedValues: boolean;
    twrPerformanceVariationCode: string;
    twrPerformanceVariationLabel: string;
    irrPerformanceVariationCode: string;
    irrPerformanceVariationLabel: string;
    groupByCommonHoldings: boolean;
    allLabelOverride: string;
    removeAllLine: boolean;
    createOtherGroups: boolean = true;
    removeEmptyComponents: boolean = true;
    get targetCurrencyId(): number {
      if (this.targetCurrency){
        return this.targetCurrency.id;
      }
      return null;
    }
    get twrSettingsSourceAssetResourceId(): number {
      if (this.twrSettingsSourceAsset){
        return this.twrSettingsSourceAsset.resourceId;
      }
      return null;
    }
  }

  export class AdvancedOptionsParameterControl extends Solovis.UI.BaseControl {
    public static hasNewRender = true;
    private _options: Options;

    constructor(options) {
      super(options);
      this._options = (typeof options.initialValue != "undefined") ? options.initialValue : null;
    }

    public async doRender() {
      if (this.options.renderMode !== 'modern') {
        // not sure if we want to alert or show a modal or just render some text where the parameter is trying to be placed
        console.error("Horizon Performance Advanced Options can not be rendered outside the new experience.")
        return;
      }

      const target = this.container[0];
      const self = this;

      await Solovis.UX.Utils.placeComponent(
        "ConfigureControl",
        target,
        {
          onClick: self._showSourceDataEditor.bind(this)
        }
      );
    }

    private _showSourceDataEditor() {
      const editorDialog = new AdvancedOptionsEditorDialog({
        options: this._options,
        saveCallback: (data: Options) => this.setParameterValue(data),
      });
      editorDialog.show();
    }

    public _setCurrentSourceDataOptions(data) { this._options = data; }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, AdvancedOptionsParameterControl, this._setCurrentSourceDataOptions, newValue, more);
    }

    public getParameterValue() { return Solovis.deepCopy(this._options); }

    public notifyParameterValueChanged() { $(this).trigger("parameterValueChanged"); }
  }


  export class AdvancedOptionsEditorDialog {

    private _opts: Options;
    private _saveCallback;
    private formBuilder: Solovis.UI.InputControls.FormBuilder;
    private twrVariationsOptionsList: any[] = new Array();
    private irrVariationsOptionsList: any[] = new Array();

    public constructor(options: { options: Options; saveCallback: any; }) {
      this._opts = options.options ? options.options : new Options();
      this._saveCallback = options.saveCallback;
    }

    public show() {
      const options = this._opts;
      this.formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: $("<div>"),
        width: 700,
        labelColumnWidth: 220
      });

      const customEnvironmentUserData = this.formBuilder.portalServices().customEnvironmentUserData();
      // Determine variations lists.
      if (customEnvironmentUserData && customEnvironmentUserData.ModelSettings) {
        const modelSettings = customEnvironmentUserData.ModelSettings;
        if (modelSettings && modelSettings.performanceVariationsConfig && modelSettings.performanceVariationsConfig.variations) {
          for (const variation of modelSettings.performanceVariationsConfig.variations) {
            if (variation.allowForTwr) {
              this.twrVariationsOptionsList.push({ text: variation.label, value: variation.code });
            }
            if (variation.allowForIrr) {
              this.irrVariationsOptionsList.push({ text: variation.label, value: variation.code });
            }
          }
        }
      }

      this.formBuilder.addEntry({
        name: "targetCurrency",
        label: "Target Currency",
        initialValue: options.targetCurrency ? options.targetCurrency : null,
        editor: {
          type: "parameter",
          parameterOptions: { parameterType: "currency" }
        }
      });

      this.formBuilder.addEntry({
        name: "benchmark",
        label: "Benchmark",
        initialValue: options.benchmark ? "Primary" : (options.secondaryBenchmark ? "Secondary" : null),
        editor: {
          type: "select",
          selectOptions: {
            data: [
              { text: "Primary", value: "Primary" },
              { text: "Secondary", value: "Secondary" }
            ],
            showClearButton: true
          }
        }
      });

      this.formBuilder.addEntry({
        name: "benchmarkExcessReturn",
        label: "Benchmark Excess Return",
        initialValue: options.benchmarkExcessReturn,
        editor: { type: "bool" },
      });

      this.formBuilder.addEntry({
        name: "twrSettingsSourceAsset",
        label: "TWR Settings Source Asset",
        initialValue: options.twrSettingsSourceAsset,
        editor: {
          type: "parameter",
          parameterOptions: {
            parameterType: "entityOrInvestment"
          }
        }
      });

      this.formBuilder.addEntry({
        name: "bookValues",
        label: "Book Values",
        initialValue: options.bookValues,
        editor: { type: "bool" },
      });

      this.formBuilder.addEntry({
        name: "blendedValues",
        label: "Blended Values",
        initialValue: options.blendedValues,
        editor: { type: "bool" },
      });

      this.formBuilder.addEntry({
        name: "twrPerformanceVariation",
        label: "TWR Performance Variation",
        initialValue: options.twrPerformanceVariationCode,
        editor: {
          type: "select",
          selectOptions: {
            data: this.twrVariationsOptionsList,
            showClearButton: true
          }
        },
        isVisible: (entry) => { return !_.isEmpty(this.twrVariationsOptionsList); },
      });

      this.formBuilder.addEntry({
        name: "irrPerformanceVariation",
        label: "IRR Performance Variation",
        initialValue: options.irrPerformanceVariationCode,
        editor: {
          type: "select",
          selectOptions: {
            data: this.irrVariationsOptionsList,
            showClearButton: true
          }
        },
        isVisible: (entry) => { return !_.isEmpty(this.irrVariationsOptionsList); },
      });

      this.formBuilder.addEntry({
        name: "groupByCommonHoldings",
        label: "Group By Common Holdings",
        initialValue: options.groupByCommonHoldings,
        editor: { type: "bool" },
      });

      this.formBuilder.addEntry({
        name: "removeAllLine",
        label: "Remove All Line",
        initialValue: options.removeAllLine,
        editor: { type: "bool" },
      });

      this.formBuilder.addEntry({
        name: "allLabelOverride",
        label: "All Label Override",
        initialValue: options.allLabelOverride,
        editor: { type: "text" },
      });

      this.formBuilder.addEntry({
        name: "createOtherGroups",
        label: "Create Other Groups",
        initialValue: options.createOtherGroups,
        editor: { type: "bool" },
      });

      this.formBuilder.addEntry({
        name: "removeEmptyComponents",
        label: "Remove Empty Components",
        initialValue: options.removeEmptyComponents,
        editor: { type: "bool" },
      });

      this.formBuilder.setupDone();
      this.formBuilder.showAsDialog({
        title: "Advanced Options",
        desiredWidth: 720,
        desiredHeight: 525,
        showSaveCancel: true,
        saveButtonText: "OK",
        saveCallback: () => {
          const newOptions = new Options();
          newOptions.targetCurrency = this.formBuilder.getValueByName("targetCurrency");
          const bmValue = this.formBuilder.getValueByName("benchmark");
          newOptions.benchmark = bmValue === "Primary";
          newOptions.secondaryBenchmark = bmValue !== "Primary" && bmValue === "Secondary";
          newOptions.benchmarkExcessReturn = this.formBuilder.getValueByName("benchmarkExcessReturn");
          newOptions.twrSettingsSourceAsset = this.formBuilder.getValueByName("twrSettingsSourceAsset");
          newOptions.bookValues = this.formBuilder.getValueByName("bookValues");
          newOptions.blendedValues = newOptions.bookValues
            ? !!this.formBuilder.getValueByName("blendedValues")
            : false;

          newOptions.twrPerformanceVariationCode = this.formBuilder.getValueByName("twrPerformanceVariation");
          const twrPerformanceVariation = (this.twrVariationsOptionsList as any).find(variation => variation.value === newOptions.twrPerformanceVariationCode);
          newOptions.twrPerformanceVariationLabel = twrPerformanceVariation?.text;
          newOptions.irrPerformanceVariationCode = this.formBuilder.getValueByName("irrPerformanceVariation");
          const irrPerformanceVariation = (this.irrVariationsOptionsList as any).find(variation => variation.value === newOptions.irrPerformanceVariationCode);
          newOptions.irrPerformanceVariationLabel = irrPerformanceVariation?.text;

          newOptions.groupByCommonHoldings = this.formBuilder.getValueByName("groupByCommonHoldings");
          newOptions.removeAllLine = this.formBuilder.getValueByName("removeAllLine");
          newOptions.allLabelOverride = this.formBuilder.getValueByName("allLabelOverride");
          newOptions.createOtherGroups = this.formBuilder.getValueByName("createOtherGroups");
          newOptions.removeEmptyComponents = this.formBuilder.getValueByName("removeEmptyComponents");
          this._saveCallback(newOptions);
          return true;
        }
      });
    }
  }
}